import React, { useRef, useState } from "react";

const formatBytes = (bytes) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const FileUpload = ({
  label,
  name,
  accept,
  maxSizeMB,
  hint,
  error: externalError,
  onChange,
  required,
}) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [internalError, setInternalError] = useState("");

  const acceptMime = accept ? accept.join(",") : undefined;
  const maxBytes = maxSizeMB ? maxSizeMB * 1024 * 1024 : null;

  const validate = (f) => {
    if (accept && !accept.includes(f.type)) {
      return `Invalid file type. Please upload ${accept.map((a) => a.split("/")[1].toUpperCase()).join(", ")}.`;
    }
    if (maxBytes && f.size > maxBytes) {
      return `File too large. Maximum size is ${maxSizeMB} MB.`;
    }
    return null;
  };

  const handleFile = (f) => {
    const err = validate(f);
    if (err) {
      setInternalError(err);
      setFile(null);
      onChange && onChange(null);
      return;
    }
    setInternalError("");
    setFile(f);
    onChange && onChange(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleChange = (e) => {
    const f = e.target.files[0];
    if (f) handleFile(f);
  };

  const remove = () => {
    setFile(null);
    setInternalError("");
    onChange && onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const displayError = internalError || externalError;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-textPrimary mb-1.5">
          {label}
          {required && <span className="text-statusRed ml-0.5">*</span>}
        </label>
      )}

      {file ? (
        <div className="flex items-center justify-between border border-cardBorder rounded-input px-3 py-2.5 bg-pageBg">
          <div className="flex items-center gap-2 min-w-0">
            <svg className="w-4 h-4 text-brand flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span className="text-sm text-textPrimary truncate">{file.name}</span>
            <span className="text-xs text-textMuted flex-shrink-0">{formatBytes(file.size)}</span>
          </div>
          <button
            type="button"
            onClick={remove}
            className="ml-2 text-textMuted hover:text-statusRed flex-shrink-0 transition-colors"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          className={`border-2 border-dashed rounded-input px-4 py-6 flex flex-col items-center justify-center cursor-pointer transition-colors ${
            dragOver
              ? "border-brand bg-blue-50"
              : displayError
              ? "border-statusRed bg-statusRedBg"
              : "border-cardBorder bg-pageBg hover:border-brand hover:bg-blue-50"
          }`}
        >
          <svg className="w-6 h-6 text-textMuted mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="text-sm text-textMuted">
            <span className="text-brand font-medium">Click to upload</span> or drag and drop
          </p>
          {hint && <p className="text-xs text-textMuted mt-1">{hint}</p>}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={acceptMime}
        onChange={handleChange}
        className="hidden"
      />

      {displayError && (
        <p className="mt-1 text-xs text-statusRed">{displayError}</p>
      )}
    </div>
  );
};

export default FileUpload;
